import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Rocket,
  Play,
  Square,
  Pause,
  RotateCcw,
  ArrowDownToLine,
  Trash2,
  FileText,
  ArrowLeft,
  Copy,
  Key,
  X,
  Terminal,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  listAgentRuntimes,
  createAgentRuntime,
  startRuntime,
  stopRuntime,
  restartRuntime,
  unpauseRuntime,
  pauseRuntime,
  shutdownRuntime,
  deleteAgentRuntime,
  fetchRuntimeLogs,
  type AgentRuntime,
} from '../api/deployments';
import {
  listAgentApiTokens,
  createAgentApiToken,
  deleteAgentApiToken,
  type AgentApiToken,
} from '../api/agentApiTokens';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';

const FRAMEWORK_NAMES: Record<string, string> = {
  celestial_toad: 'Celestial Toad v4',
  hermes: 'Hermes',
  openclaw: 'OpenClaw',
  openhuman: 'OpenHuman',
  generic: '通用运行时 (Docker)',
  claude_code: 'Claude Code',
  codex: 'OpenAI Codex',
  hermes_agent: 'Hermes Agent',
};

const FRAMEWORK_DESC: Record<string, string> = {
  celestial_toad: '原生 CT v4 容器，端口 20001',
  hermes: 'HTTP Agent 服务',
  openclaw: 'MCP 工具服务器',
  openhuman: 'WebSocket 流式服务',
  generic: '统一运行时镜像，内置 /health /metrics',
  claude_code: 'Claude Code 项目配置 (.claude/)',
  codex: 'OpenAI Codex 项目配置 (.codex/)',
  hermes_agent: 'Hermes Agent 轻量运行时',
};

const getApiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

export default function AgentDeployments() {
  const params = useParams();
  // 路由参数按存在处理：缺省时保持与原模板字符串一致的 `/agents/undefined/...` 请求行为
  const agentId = params.agentId as string;
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [runtimes, setRuntimes] = useState<AgentRuntime[]>([]);
  const [tokens, setTokens] = useState<AgentApiToken[]>([]);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [logs, setLogs] = useState<string[] | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState('generic');

  const loadRuntimes = useCallback(async () => {
    try {
      const list = await listAgentRuntimes(agentId);
      setRuntimes(list || []);
    } catch (e) {
      console.error('Failed to load runtimes:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  const loadTokens = useCallback(async () => {
    if (!agentId) return;
    try {
      const list = await listAgentApiTokens(agentId);
      setTokens(list || []);
    } catch (e) {
      console.error('Failed to load tokens:', e);
    }
  }, [agentId]);

  // 渲染期同步：切换 Agent 时隐藏上一个 Agent 的新 Token（替代在 effect 中同步 setState）
  const [prevAgentId, setPrevAgentId] = useState(agentId);
  if (prevAgentId !== agentId) {
    setPrevAgentId(agentId);
    setNewToken(null);
  }

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadRuntimes();
      void loadTokens();
    };
    tick();
  }, [loadRuntimes, loadTokens]);

  const generateToken = async () => {
    if (!agentId) return;
    setTokenLoading(true);
    try {
      const created = await createAgentApiToken(agentId, { name: 'frontend-runtime' });
      setNewToken(created.token);
      await loadTokens();
      toast.success('Token 已生成，请立即复制');
    } catch (e) {
      toast.error('生成 Token 失败: ' + getApiErrorMessage(e));
    } finally {
      setTokenLoading(false);
    }
  };

  const revokeToken = async (tokenId: string) => {
    if (!agentId) return;
    if (!(await confirm({ title: '确定要吊销该 Token 吗？', message: '吊销后无法恢复。', danger: true, confirmText: '吊销' }))) return;
    try {
      await deleteAgentApiToken(agentId, tokenId);
      await loadTokens();
      toast.success('Token 已吊销');
    } catch (e) {
      toast.error('吊销失败: ' + getApiErrorMessage(e));
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('已复制');
    } catch {
      toast.error('复制失败，请手动复制');
    }
  };

  const handleCreateRuntime = async () => {
    if (!(await confirm({ title: `确定要使用「${FRAMEWORK_NAMES[selectedFramework]}」框架部署此 Agent 吗？` }))) return;
    setActionLoading('create');
    try {
      await createAgentRuntime(agentId, selectedFramework);
      await loadRuntimes();
    } catch (e) {
      toast.error('部署失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartRuntime = async () => {
    setActionLoading('start');
    try {
      await startRuntime(agentId);
      await loadRuntimes();
    } catch (e) {
      toast.error('启动失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopRuntime = async () => {
    if (!(await confirm({ title: '确定停止？', message: '停止后 Agent 将不可用，但资源保留。', danger: true }))) return;
    setActionLoading('stop');
    try {
      await stopRuntime(agentId);
      await loadRuntimes();
    } catch (e) {
      toast.error('停止失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestartRuntime = async () => {
    setActionLoading('restart');
    try {
      await restartRuntime(agentId);
      await loadRuntimes();
    } catch (e) {
      toast.error('重启失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpauseRuntime = async () => {
    setActionLoading('unpause');
    try {
      await unpauseRuntime(agentId);
      await loadRuntimes();
    } catch (e) {
      toast.error('恢复失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseRuntime = async () => {
    if (!(await confirm({ title: '确定暂停？', message: '暂停后 Agent 将冻结（保留内存）。', danger: true }))) return;
    setActionLoading('pause');
    try {
      await pauseRuntime(agentId);
      await loadRuntimes();
    } catch (e) {
      toast.error('暂停失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleShutdownRuntime = async () => {
    if (!(await confirm({ title: '确定下线？', message: '下线将释放沙箱资源，并导出 Agent 数据到归档。', danger: true }))) return;
    setActionLoading('shutdown');
    try {
      await shutdownRuntime(agentId, { export_data: true, reason: 'user_request' });
      await loadRuntimes();
    } catch (e) {
      toast.error('下线失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteRuntime = async () => {
    if (
      !(await confirm({
        title: '确定彻底删除？',
        message: '彻底删除将不可恢复！',
        danger: true,
        confirmText: '删除',
        requireText: '彻底删除',
      }))
    )
      return;
    setActionLoading('delete');
    try {
      await deleteAgentRuntime(agentId);
      await loadRuntimes();
    } catch (e) {
      toast.error('删除失败: ' + getApiErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const fetchLogs = async () => {
    try {
      const list = await fetchRuntimeLogs(agentId);
      setLogs(list || []);
      setShowLogs(true);
    } catch (e) {
      console.error('Failed to load logs:', e);
    }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      running: 'success',
      paused: 'warning',
      stopped: 'info',
      error: 'error',
      pending: 'warning',
      deploying: 'warning',
      offline: 'secondary',
    };
    return <span className={`badge ${map[status] || 'info'}`}>{status}</span>;
  };

  const activeRuntime = runtimes.find((r) => r.status !== 'offline');
  const agentNameForModel = activeRuntime
    ? activeRuntime.agent_name || 'agent'
    : 'agent';
  const curlExample = `curl -H "Authorization: Bearer ${newToken || '<TOKEN>'}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model":"${agentNameForModel}","messages":[{"role":"user","content":"你好"}]}' \\\n  http://localhost:25101/api/v1/chat/completions`;

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <button className="secondary mb-4" onClick={() => navigate(`/agents/${agentId}`)}>
        <ArrowLeft size={16} /> 返回概览
      </button>

      <h1>
        <Rocket size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />
        Agent 部署管理
      </h1>
      <p className="text-muted">选择框架，一键启动真实 Docker 容器</p>

      {/* Framework selector + Create */}
      {!activeRuntime && (
        <div className="card mb-4">
          <h3>选择部署框架</h3>
          <div className="grid grid-2 gap-4 mt-4">
            {Object.entries(FRAMEWORK_NAMES).map(([fw, name]) => (
              <div
                key={fw}
                className={`framework-card ${selectedFramework === fw ? 'selected' : ''}`}
                onClick={() => setSelectedFramework(fw)}
              >
                <h4 style={{ margin: '0 0 4px' }}>{name}</h4>
                <p className="text-muted text-sm" style={{ margin: 0 }}>
                  {FRAMEWORK_DESC[fw]}
                </p>
                {selectedFramework === fw && (
                  <span className="badge success mt-2">已选择</span>
                )}
              </div>
            ))}
          </div>
          <button
            className="primary mt-4"
            onClick={handleCreateRuntime}
            disabled={actionLoading === 'create'}
            style={{ width: '100%' }}
          >
            {actionLoading === 'create' ? '部署中...' : <><Rocket size={14} /> 创建部署</>}
          </button>
        </div>
      )}

      {/* Action bar for active runtime */}
      {activeRuntime && (
        <div className="card mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {(activeRuntime.status === 'pending' || activeRuntime.status === 'deploying') && (
              <span
                className="text-muted"
                style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <span className="spinner" style={{ width: 16, height: 16 }} />
                正在{activeRuntime.status === 'deploying' ? '部署' : '初始化'}容器，请稍候…
              </span>
            )}
            {activeRuntime.status === 'stopped' && (
              <button className="primary" onClick={handleStartRuntime} disabled={actionLoading === 'start'}>
                {actionLoading === 'start' ? '启动中...' : <><Play size={14} /> 启动</>}
              </button>
            )}
            {activeRuntime.status === 'running' && (
              <>
                <button className="secondary" onClick={handlePauseRuntime} disabled={actionLoading === 'pause'}>
                  {actionLoading === 'pause' ? '暂停中...' : <><Pause size={14} /> 暂停</>}
                </button>
                <button className="secondary" onClick={handleStopRuntime} disabled={actionLoading === 'stop'}>
                  {actionLoading === 'stop' ? '停止中...' : <><Square size={14} /> 停止</>}
                </button>
                <button className="secondary" onClick={handleRestartRuntime} disabled={actionLoading === 'restart'}>
                  {actionLoading === 'restart' ? '重启中...' : <><RotateCcw size={14} /> 重启</>}
                </button>
              </>
            )}
            {activeRuntime.status === 'paused' && (
              <>
                <button className="primary" onClick={handleUnpauseRuntime} disabled={actionLoading === 'unpause'}>
                  {actionLoading === 'unpause' ? '恢复中...' : <><Play size={14} /> 恢复</>}
                </button>
                <button className="secondary" onClick={handleStopRuntime} disabled={actionLoading === 'stop'}>
                  {actionLoading === 'stop' ? '停止中...' : <><Square size={14} /> 停止</>}
                </button>
              </>
            )}
            {activeRuntime.status === 'error' && (
              <button className="primary" onClick={handleRestartRuntime} disabled={actionLoading === 'restart'}>
                {actionLoading === 'restart' ? '重启中...' : <><RotateCcw size={14} /> 重启</>}
              </button>
            )}
            {activeRuntime.status !== 'pending' && activeRuntime.status !== 'deploying' && (
              <button className="secondary" onClick={fetchLogs}>
                <FileText size={14} /> 日志
              </button>
            )}
            {activeRuntime.status !== 'pending' && activeRuntime.status !== 'deploying' && (
              <button
                className="secondary"
                onClick={handleShutdownRuntime}
                disabled={actionLoading === 'shutdown'}
              >
                {actionLoading === 'shutdown' ? '下线中...' : <><ArrowDownToLine size={14} /> 下线</>}
              </button>
            )}
            {activeRuntime.status !== 'pending' && activeRuntime.status !== 'deploying' && (
              <button className="secondary" onClick={handleDeleteRuntime} disabled={actionLoading === 'delete'}>
                {actionLoading === 'delete' ? '删除中...' : <><Trash2 size={14} /> 删除</>}
              </button>
            )}
            <button className="secondary" onClick={generateToken} disabled={tokenLoading}>
              {tokenLoading ? '生成中...' : <><Key size={14} /> 生成 Token</>}
            </button>
          </div>
        </div>
      )}

      {/* Runtime status */}
      {activeRuntime && (
        <div className="card mb-4">
          <h3>当前运行时</h3>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {getStatusBadge(activeRuntime.status)}
            {activeRuntime.runtime_info?.stub && (
              <span className="badge warning" title={activeRuntime.runtime_info?.fallback_reason || 'Docker 启动失败，当前为 stub 假运行'}>stub</span>
            )}
            <span>
              <strong>框架:</strong> {FRAMEWORK_NAMES[activeRuntime.framework] || activeRuntime.framework}
            </span>
            <span>
              <strong>沙箱:</strong> {activeRuntime.sandbox_id?.slice(0, 8) || '-'}
            </span>
          </div>

          <div className="grid grid-3 gap-4 mb-4">
            <div className="card" style={{ padding: 12 }}>
              <p className="text-muted text-sm" style={{ margin: 0 }}>
                容器 ID
              </p>
              <p style={{ margin: '4px 0 0', fontFamily: 'monospace', fontSize: 13 }}>
                {activeRuntime.container_id ? `${activeRuntime.container_id.slice(0, 12)}...` : '-'}
              </p>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <p className="text-muted text-sm" style={{ margin: 0 }}>
                端口
              </p>
              <p style={{ margin: '4px 0 0', fontFamily: 'monospace', fontSize: 13 }}>
                {activeRuntime.port || '-'}
              </p>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <p className="text-muted text-sm" style={{ margin: 0 }}>
                端点
              </p>
              <p
                style={{
                  margin: '4px 0 0',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  wordBreak: 'break-all',
                }}
              >
                {activeRuntime.endpoint_url || '-'}
              </p>
            </div>
          </div>

          {activeRuntime.runtime_info && (
            <div className="grid grid-3 gap-4 mb-4">
              {activeRuntime.runtime_info.healthy !== undefined && (
                <div className="card" style={{ padding: 12 }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>
                    健康状态
                  </p>
                  <p style={{ margin: '4px 0 0' }}>
                    {activeRuntime.runtime_info.healthy ? (
                      <span className="badge success"><CheckCircle size={12} style={{ marginRight: 4 }} />健康</span>
                    ) : (
                      <span className="badge error"><XCircle size={12} style={{ marginRight: 4 }} />异常</span>
                    )}
                  </p>
                </div>
              )}
              {activeRuntime.runtime_info.cpu_percent !== undefined && (
                <div className="card" style={{ padding: 12 }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>
                    CPU
                  </p>
                  <p style={{ margin: '4px 0 0', fontFamily: 'monospace' }}>
                    {activeRuntime.runtime_info.cpu_percent}%
                  </p>
                </div>
              )}
              {activeRuntime.runtime_info.memory_mb !== undefined && (
                <div className="card" style={{ padding: 12 }}>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>
                    内存
                  </p>
                  <p style={{ margin: '4px 0 0', fontFamily: 'monospace' }}>
                    {activeRuntime.runtime_info.memory_mb} MB
                  </p>
                </div>
              )}
            </div>
          )}

          {activeRuntime.error_message && (
            <div className="card" style={{ borderColor: 'var(--color-error)', padding: 12 }}>
              <p className="text-sm" style={{ color: 'var(--color-error)', margin: 0 }}>
                <strong>错误:</strong> {activeRuntime.error_message}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Token management */}
      <div className="card mb-4">
        <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0 }}>API 访问 Token</h3>
            <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
              用于调用 OpenAI 兼容接口，Token 仅在创建时显示一次
            </p>
          </div>
          <button className="secondary" onClick={generateToken} disabled={tokenLoading}>
            {tokenLoading ? '生成中...' : <><Key size={14} /> 生成新 Token</>}
          </button>
        </div>

        {newToken && (
          <div
            className="card mt-4"
            style={{ background: 'rgba(34, 197, 94, 0.08)', borderColor: 'var(--color-success)' }}
          >
            <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
              <strong style={{ color: 'var(--color-success)' }}>新 Token（请立即复制）</strong>
              <button className="text-sm" onClick={() => setNewToken(null)}>
                <X size={14} /> 隐藏
              </button>
            </div>
            <div className="flex items-center gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
              <input
                type="text"
                value={newToken}
                readOnly
                style={{ flex: 1, minWidth: 260, fontFamily: 'monospace', fontSize: 13 }}
              />
              <button className="secondary" onClick={() => copyToClipboard(newToken)}>
                <Copy size={14} /> 复制
              </button>
            </div>
            <div className="mt-4">
              <p className="text-sm" style={{ marginBottom: 6 }}>
                <Terminal size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                调用示例
              </p>
              <pre
                style={{
                  background: '#1e1e1e',
                  color: '#d4d4d4',
                  padding: 12,
                  borderRadius: 6,
                  fontSize: 12,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  lineHeight: 1.5,
                }}
              >
                {curlExample}
              </pre>
            </div>
          </div>
        )}

        {tokens.length === 0 ? (
          <div className="empty mt-4">
            <p>暂无 Token</p>
          </div>
        ) : (
          <div className="list mt-4">
            {tokens.map((token) => (
              <div key={token.id} className="list-item">
                <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong>{token.name || '未命名 Token'}</strong>
                    {!token.is_active && <span className="badge error ml-2">已吊销</span>}
                    {token.is_active && <span className="badge success ml-2">有效</span>}
                    <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                      创建: {new Date(token.created_at).toLocaleString()}
                      {token.expires_at && ` · 过期: ${new Date(token.expires_at).toLocaleString()}`}
                    </p>
                  </div>
                  {token.is_active && (
                    <button className="secondary text-sm" onClick={() => revokeToken(token.id)}>
                      吊销
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Logs */}
      {showLogs && logs && (
        <div className="card mb-4">
          <div className="flex items-center justify-between">
            <h3>容器日志</h3>
            <button className="secondary" onClick={() => setShowLogs(false)}>
              关闭
            </button>
          </div>
          <pre
            style={{
              background: '#1e1e1e',
              color: '#d4d4d4',
              padding: 12,
              borderRadius: 6,
              fontSize: 12,
              maxHeight: 400,
              overflow: 'auto',
              fontFamily: 'monospace',
              lineHeight: 1.5,
            }}
          >
            {logs.join('\n')}
          </pre>
        </div>
      )}

      {/* Runtime history */}
      <div className="card">
        <h3>运行时历史</h3>
        {runtimes.length === 0 ? (
          <div className="empty">
            <p>暂无运行时记录</p>
          </div>
        ) : (
          <div className="list" style={{ marginTop: 16 }}>
            {runtimes.map((rt) => (
              <div key={rt.id} className="list-item">
                <div className="list-item-content">
                  <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                    <span className="text-sm text-muted">{rt.id.slice(0, 8)}</span>
                    {getStatusBadge(rt.status)}
                    {rt.runtime_info?.stub && (
                      <span className="badge warning" title={rt.runtime_info?.fallback_reason || 'Docker 启动失败，当前为 stub 假运行'}>stub</span>
                    )}
                    <span className="badge info">{FRAMEWORK_NAMES[rt.framework] || rt.framework}</span>
                    {rt.container_id && (
                      <span className="text-sm text-muted" style={{ fontFamily: 'monospace, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji"' }}>
                        {rt.container_id.slice(0, 12)}
                      </span>
                    )}
                    {rt.port && <span className="badge warning">:{rt.port}</span>}
                  </div>
                  {rt.endpoint_url && (
                    <p
                      className="text-muted text-sm"
                      style={{ margin: '4px 0 0', fontFamily: 'monospace' }}
                    >
                      {rt.endpoint_url}
                    </p>
                  )}
                  <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                    {rt.deployed_at ? `部署: ${new Date(rt.deployed_at).toLocaleString()}` : ''}
                    {rt.stopped_at ? ` · 停止: ${new Date(rt.stopped_at).toLocaleString()}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {dialog}
    </div>
  );
}
